import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useApp } from "../contexts/AppContext";
import { useLanguage } from "../contexts/LanguageContext";
import { RootState } from "../store";
import { useGetProjectQuery, useGetProjectMembersQuery, useRemoveProjectMemberMutation } from "../store/api/projects";
import { useGetProjectTasksQuery } from "../store/api/tasks";
import { useGetWorkspaceMembersQuery } from "../store/api/workspaces";
import { toast } from "sonner";
import { handleApiError } from "../utils/apiErrors";
import { Button } from "../components/ui/button";
import { Loader2 } from "lucide-react";
import {
  ArrowLeft,
  Plus,
  ChevronDown,
  FileText,
  Zap,
  Users,
  UserPlus,
  X,
} from "lucide-react";
import TaskListView from "../components/TaskListView";
import AddTasksFromTemplateModal from "../components/AddTasksFromTemplateModal";
import QuickAddTaskModal from "../components/QuickAddTaskModal";
import FilterPanel from "../components/FilterPanel";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import InviteGuestModal from "../components/InviteGuestModal";
import { startOfDay, endOfDay } from "date-fns";
import { parseApiDate } from "../utils/date";
import { isOverdue } from "../utils/taskGenerator";
import { WORKSPACE_DEFAULT_TIMEZONE } from "../utils/constants";

export default function ProjectTasksPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { filters, setFilters } = useApp();
  const { t, language } = useLanguage();
  
  // Get current workspace from Redux
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const workspaceId = currentWorkspace?.id || '';
  const workspaceTimeZone = currentWorkspace?.timezone || WORKSPACE_DEFAULT_TIMEZONE;

  // Fetch project from API
  const { data: project, isLoading: isLoadingProject } = useGetProjectQuery(
    { workspaceId, projectId: projectId! },
    { skip: !workspaceId || !projectId }
  );

  // Fetch project tasks from API
  const { data: projectTasks = [], isLoading: isLoadingTasks } = useGetProjectTasksQuery(
    { workspaceId, projectId: projectId! },
    { skip: !workspaceId || !projectId }
  );

  // Fetch all workspace users from API (for general user info display)
  // Refetch on mount to ensure latest data
  const { data: users = [] } = useGetWorkspaceMembersQuery(
    workspaceId,
    { 
      skip: !workspaceId,
      refetchOnMountOrArgChange: true 
    }
  );

  // Fetch project members directly from API (includes guests)
  // Refetch on mount to ensure latest data
  const { data: projectMembers = [] } = useGetProjectMembersQuery(
    { workspaceId, projectId: projectId! },
    { 
      skip: !workspaceId || !projectId,
      refetchOnMountOrArgChange: true 
    }
  );

  // Remove project member mutation
  const [removeProjectMemberMutation] = useRemoveProjectMemberMutation();

  const [isAddFromTemplateModalOpen, setIsAddFromTemplateModalOpen] = useState(false);
  const [isQuickAddModalOpen, setIsQuickAddModalOpen] = useState(false);
  const [isInviteGuestModalOpen, setIsInviteGuestModalOpen] = useState(false);

  // Set default filters when entering the project details page
  useEffect(() => {
    setFilters({
      ...filters,
      projectIds: projectId ? [projectId] : [],
      status: 'all',
      taskType: 'all',
      assigneeIds: [],
      dateRange: { type: 'all' },
    });
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Convert ProjectTask[] to TaskInstance format compatible with TaskListView
  // ProjectTask has dueDate/completedAt as strings, but TaskInstance needs Date objects
  const projectTaskInstances = useMemo(() => {
    return projectTasks.map(task => {
      // Convert date strings to Date objects using local parsing
      const dueDate = task.dueDate ? parseApiDate(task.dueDate) : new Date();
      const completedAt = task.completedAt ? new Date(task.completedAt) : undefined;
      
      // Create repeatPattern object for TaskListView
      const repeatPattern = task.isRepeatable ? {
        type: task.repeatType,
        initialDueDate: dueDate,
        useMonthEnd: task.useMonthEnd || false,
      } : { type: 'none' as const };
      
      return {
        ...task,
        dueDate,
        completedAt,
        // Add routineTaskId for backward compatibility (used by TaskListView)
        // Set to task.id if repeatable so TaskListView can find it in routineTasks
        routineTaskId: task.isRepeatable ? task.id : null,
        // Add repeatPattern directly to the task so TaskListView can use it
        repeatPattern,
        // Ensure occurrenceDate exists (used by some components)
        occurrenceDate: task.createdAt ? new Date(task.createdAt) : new Date(),
      };
    });
  }, [projectTasks]);

  // フィルタークリック処理
  const handleFilterClick = (type: 'all' | 'pending' | 'overdue' | 'completed') => {
    if (type === 'all') {
      setFilters({
        projectIds: [projectId!],
        dateRange: { type: 'today' },
        assigneeIds: [],
        status: 'all',
      });
    } else if (type === 'pending') {
      setFilters({
        projectIds: [projectId!],
        dateRange: { type: 'today' },
        assigneeIds: [],
        status: 'pending',
      });
    } else if (type === 'overdue') {
      setFilters({
        projectIds: [projectId!],
        dateRange: { type: 'overdue' },
        assigneeIds: [],
        status: 'pending',
      });
    } else if (type === 'completed') {
      setFilters({
        projectIds: [projectId!],
        dateRange: { type: 'today' },
        assigneeIds: [],
        status: 'completed',
      });
    }
  };

  // フィルター適用
  const filteredTasks = useMemo(() => {
    let filtered = projectTaskInstances;

    // 期限フィルター - only apply if not 'all'
    if (filters.dateRange.type !== 'all') {
      // カスタム期間
    if (filters.dateRange.customStart || filters.dateRange.customEnd) {
      const start = filters.dateRange.customStart
        ? startOfDay(filters.dateRange.customStart)
        : null;
      const end = filters.dateRange.customEnd
        ? endOfDay(filters.dateRange.customEnd)
        : null;

      filtered = filtered.filter((task) => {
        const taskDue = task.dueDate;
        if (start && end) {
          return taskDue >= start && taskDue <= end;
        } else if (start) {
          return taskDue >= start;
        } else if (end) {
          return taskDue <= end;
        }
        return true;
      });
    }

    // 期限切れフィルター
    if (filters.dateRange.type === "overdue") {
        filtered = filtered.filter(
          (task) => task.status === "pending" && isOverdue(task, workspaceTimeZone)
        );
      }
    }

    // 担当者フィルター
    if (filters.assigneeIds.length > 0) {
      filtered = filtered.filter((task) =>
        task.assigneeIds.some((id) => filters.assigneeIds.includes(id))
      );
    }

    // ステータスフィルター
    if (filters.status !== "all") {
      filtered = filtered.filter((task) => task.status === filters.status);
    }

    // タスクタイプフィルター
    if (filters.taskType && filters.taskType !== "all") {
      if (filters.taskType === "recurring") {
        filtered = filtered.filter((task) => task.isRepeatable);
      } else if (filters.taskType === "oneTime") {
        filtered = filtered.filter((task) => !task.isRepeatable);
      }
    }

    // 期限日でソート（期限が近い順、期限なしは最後）
    return filtered.sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return a.dueDate.getTime() - b.dueDate.getTime();
    });
  }, [projectTaskInstances, filters, workspaceTimeZone]);

  // 統計情報
  const stats = useMemo(() => {
    const pending = projectTaskInstances.filter((t) => t.status === "pending");
    const overdue = pending.filter((t) => isOverdue(t, workspaceTimeZone));
    const recurring = projectTaskInstances.filter((t) => t.isRepeatable);
    const oneTime = projectTaskInstances.filter((t) => !t.isRepeatable);

    return {
      pending: pending.length,
      overdue: overdue.length,
      recurring: recurring.length,
      oneTime: oneTime.length,
    };
  }, [projectTaskInstances, workspaceTimeZone]);

  const handleRemoveMember = async (userId: string) => {
    if (!workspaceId || !projectId) return;
    if (confirm(t('projects.removeMemberConfirm') || 'Are you sure you want to remove this member?')) {
      try {
        await removeProjectMemberMutation({
          workspaceId,
          projectId,
          userId,
        }).unwrap();
        toast.success(t('projects.memberRemoved') || 'Member removed successfully');
      } catch (error) {
        console.error('Failed to remove member:', error);
        handleApiError(error, t('projects.removeMemberFailed') || 'Failed to remove member');
      }
    }
  };

  if (isLoadingProject) {
    return (
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">{t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{t('projects.projectNotFound')}</p>
          <Button onClick={() => navigate("/projects")}>
            {t('projects.backToProjects')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <div className="flex-shrink-0 max-w-[1600px] mx-auto w-full px-6 pt-8 pb-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/projects")}
                className="mr-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t('common.back')}
              </Button>
              <h1 className="text-gray-900">{project.name}</h1>
            </div>
            <p className="text-gray-600">
              {project.description || t('projects.manageProjectTasks')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsInviteGuestModalOpen(true)}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {t('projects.inviteGuest')}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Users className="w-4 h-4 mr-2" />
                  {t('projects.members')}
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {projectMembers.map((user) => (
                  <DropdownMenuItem
                    key={user.id}
                    className="cursor-pointer flex items-center gap-2"
                  >
                    <div className="w-4 h-4 rounded-full bg-gray-300 mr-2"></div>
                    <div className="flex items-center gap-1">
                      {user.name}
                      {user.role === 'guest' && (
                        <UserPlus className="w-3 h-3 text-blue-600" title="Guest" />
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto"
                      onClick={() => handleRemoveMember(user.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden max-w-[1600px] mx-auto w-full px-6 pb-8">
        <div className="grid grid-cols-[280px_1fr] gap-6 h-full">
          {/* フィルターパネル - 独立スクロール */}
          <div className="overflow-y-auto h-full">
            <FilterPanel hideProjectFilter={true} projectMembers={projectMembers} />
          </div>

          {/* タスクリスト - 独立スクロール */}
          <div className="overflow-y-auto h-full">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-600">
                {filteredTasks.length}{t('projects.showingTasks')}
              </p>
              
              {/* タスク追加ドロップダウンメニュー */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    {t('projects.addTask')}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem
                    onClick={() => setIsQuickAddModalOpen(true)}
                    className="cursor-pointer"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    <div>
                      <div>{t('projects.addTaskAny')}</div>
                      <div className="text-xs text-gray-600">
                        {t('projects.createTaskFree')}
                      </div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setIsAddFromTemplateModalOpen(true)}
                    className="cursor-pointer"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    <div>
                      <div>{t('projects.addFromTemplate')}</div>
                      <div className="text-xs text-gray-600">
                        {t('projects.selectTemplateTask')}
                      </div>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {isLoadingTasks ? (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">{t('common.loading') || 'Loading tasks...'}</p>
              </div>
            ) : (
              <TaskListView tasks={filteredTasks as any} users={users} projectMembers={projectMembers} />
            )}
          </div>
        </div>
      </div>

      {/* タスクテンプレートから追加モーダル */}
      <AddTasksFromTemplateModal
        open={isAddFromTemplateModalOpen}
        onOpenChange={setIsAddFromTemplateModalOpen}
        projectId={projectId!}
      />

      {/* タスクのクイック追加モーダル */}
      <QuickAddTaskModal
        open={isQuickAddModalOpen}
        onOpenChange={setIsQuickAddModalOpen}
        projectId={projectId!}
        projectMembers={projectMembers}
      />

      {/* ゲスト招待モーダル */}
      <InviteGuestModal
        open={isInviteGuestModalOpen}
        onOpenChange={setIsInviteGuestModalOpen}
        workspaceId={workspaceId}
        projectId={projectId!}
        projectName={project.name}
      />
    </div>
  );
}
